import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getInfiniteQueryListNextParam } from '@/utils/list';

import {
  createOilLog,
  deleteOilLog,
  getOilLogById,
  getOilLogPdf,
  getOilLogs,
  getOilLogsByDate,
  updateOilLog,
} from './oil-log.api';
import type { CreateOilLogFormValues, UpdateOilLogFormValues } from './oil-log.dto';

const ROOT_KEY = 'oil-logs';

export const useOilLogsQuery = (limit: number) =>
  useInfiniteQuery({
    queryKey: [ROOT_KEY, limit],
    queryFn: ({ pageParam }) => getOilLogs(limit, pageParam),
    initialPageParam: 1,
    getNextPageParam: getInfiniteQueryListNextParam,
  });

export const useOilLogByIdQuery = (id: string | null | undefined) =>
  useQuery({
    queryKey: [ROOT_KEY, 'by-id', id],
    queryFn: () => getOilLogById(id!),
    enabled: !!id,
  });

export const useOilLogsByDateQuery = (date: string | null | undefined) =>
  useQuery({
    queryKey: [ROOT_KEY, 'by-date', date],
    queryFn: () => getOilLogsByDate(date!),
    enabled: !!date,
  });

export const useCreateOilLogMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: CreateOilLogFormValues) => createOilLog(values),
    onSuccess(_, { date }) {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'by-date', date] });
    },
  });
};

export const useUpdateOilLogMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: UpdateOilLogFormValues) => updateOilLog(values),
    onSuccess(_, { id }) {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'by-id', id] });
    },
  });
};

export const useDeleteOilLogMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteOilLog(id),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
    },
  });
};

export const useOilLogPdfMutation = () =>
  useMutation({
    mutationFn: ({ id, timezone }: { id: string; timezone?: string }) => getOilLogPdf(id, timezone),
  });
